import { VIDEO_TOOL_LABELS } from "~/common/constants"
import type { ComponentType } from "react"
import { useEffect, useRef, useState } from "react"
import { ArrowRightOutlined } from "@ant-design/icons"
import { Button, Flex } from "antd"
import classNames from "classnames"
import { productShowcaseVideos } from "~/common/inspiration/showcase"
import { ROUTER } from "~/common/router"
import type { VideoTemplate } from "~/common/videoTemplate"
import { useVideoTemplate } from "~/store/videoTemplates"
import {
  AnalyticsAction,
  MicroEditIcon,
  HighFidelityIcon,
  TemplateIcon,
  Text,
  VideoPlayer,
  VideoPreview,
} from "~/components"
import styles from "./index.css"
import { useNavigate } from "react-router"

type RecreateToolEntry = {
  actionLabel: string
  color: "primary" | "purple" | "cyan"
  description: string
  icon: ComponentType
  media: readonly { label: string; poster: string; url: string }[]
  path: string
  title: string
}

const createEntries = (
  videoTemplates: VideoTemplate[],
): readonly RecreateToolEntry[] => {
  const petSnack = videoTemplates.find(
    (item) => item.templateId === "PetSnackBegging",
  )

  return [
    {
      actionLabel: "去复刻",
      description:
        "保留参考视频的节奏、卖点结构，AI 根据新商品自适应调整场景、动作和画面表现，生成相似但不雷同的新视频",
      icon: MicroEditIcon,
      color: "primary",
      media: [
        {
          label: "参考视频",
          poster:
            "https://cdn-testing.duobaogongxiao.com/kada-uploads/2026/09/1789440732966-cpzu2nh5-origin-cover.jpg",
          url: "https://cdn-testing.duobaogongxiao.com/kada-uploads/2026/09/1789441301983-exuexeog-origin.mp4",
        },
        {
          label: "复刻视频",
          poster: petSnack?.videoCover || "",
          url: petSnack?.videoUrl || "",
        },
      ],
      path: ROUTER.RECREATE,
      title: VIDEO_TOOL_LABELS.recreate,
    },
    {
      actionLabel: "去复刻",
      description:
        "高度还原场景、镜头和人物动作，仅替换商品，快速生成同款视频（适合服饰、鞋包、饰品等商品展示）",
      icon: HighFidelityIcon,
      color: "purple",
      media: [
        {
          label: "参考视频",
          poster: productShowcaseVideos[0].poster,
          url: productShowcaseVideos[0].url,
        },
        {
          label: "复刻视频",
          poster: productShowcaseVideos[1].poster,
          url: productShowcaseVideos[1].url,
        },
      ],
      path: ROUTER.RECREATE_V2,
      title: VIDEO_TOOL_LABELS.frameCopy,
    },
    {
      actionLabel: "更多视频模板",
      description:
        "零门槛极简出片，套用内置视频模板，替换商品素材即可一键成片，成片效果更稳定",
      icon: TemplateIcon,
      color: "cyan",
      media: ["womenOutfitBeatChange", "slippersPerson"].flatMap(
        (templateId) => {
          const card = videoTemplates.find(
            (item) => item.templateId === templateId,
          )
          return card
            ? [
                {
                  label: "视频模板示例",
                  poster: card.videoCover || "",
                  url: card.videoUrl,
                },
              ]
            : []
        },
      ),
      path: ROUTER.ONE_CLICK_COPY,
      title: VIDEO_TOOL_LABELS.oneClickCopy,
    },
  ]
}

const HoverVideo = ({ url }: { url: string }) => {
  const ref = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)

  useEffect(() => {
    const video = ref.current
    if (!video) return

    video.muted = true
    void video.play().catch(() => {
      // Interrupted or blocked previews keep the cover visible.
      setPlaying(false)
    })
    return () => video.pause()
  }, [url])

  return (
    <VideoPlayer
      ref={ref}
      className={classNames(styles.video, playing && styles.videoPlaying)}
      src={url}
      muted
      loop
      playsInline
      aria-hidden
      onPlaying={() => setPlaying(true)}
      onError={() => setPlaying(false)}
    />
  )
}

export const RecreateToolSection = () => {
  const navigate = useNavigate()
  const petSnack = useVideoTemplate("PetSnackBegging").template
  const outfit = useVideoTemplate("womenOutfitBeatChange").template
  const slippers = useVideoTemplate("slippersPerson").template
  const entries = createEntries(
    [petSnack, outfit, slippers].filter((template): template is VideoTemplate =>
      Boolean(template),
    ),
  )
  const [hovered, setHovered] = useState<RecreateToolEntry["color"] | null>(
    null,
  )

  return (
    <section className={styles.section}>
      <Text
        size={18}
        strong
        className={styles.sectionTitle}
      >
        爆款复刻工具
      </Text>
      <Flex
        className={styles.grid}
        gap={20}
        wrap
      >
        {entries.map((entry) => {
          const Icon = entry.icon

          return (
            <AnalyticsAction
              action={`点击${entry.title}`}
              key={entry.color}
              prefix="首页爆款复刻工具"
            >
              <Flex
                className={classNames(styles.card, styles[entry.color])}
                vertical
                onClick={() => navigate(entry.path)}
                onMouseEnter={() => setHovered(entry.color)}
                onMouseLeave={() => setHovered(null)}
              >
                <Flex
                  className={styles.cardContent}
                  gap={10}
                  vertical
                  justify="space-between"
                >
                  <Flex
                    align="center"
                    gap={10}
                  >
                    <Flex
                      className={styles.iconShell}
                      align="center"
                      justify="center"
                    >
                      <Icon />
                    </Flex>
                    <Text
                      size="l"
                      strong
                    >
                      {entry.title}
                    </Text>
                  </Flex>
                  <Text type="secondary">{entry.description}</Text>
                  <Flex gap={20}>
                    {entry.media.map((item, index) => (
                      <div
                        className={styles.mediaItem}
                        key={`${item.poster}-${index}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <VideoPreview
                          alt={`${entry.title} · ${item.label}`}
                          className={styles.mediaPreview}
                          poster={item.poster}
                          showPlayIcon={false}
                          src={item.url}
                          width="100%"
                        />
                        {hovered === entry.color && (
                          <HoverVideo url={item.url} />
                        )}
                        <Text
                          size="s"
                          color="var(--ant-color-text-light-solid)"
                          className={styles.mediaLabel}
                        >
                          {item.label}
                        </Text>
                      </div>
                    ))}
                  </Flex>
                  <Button
                    color={entry.color}
                    variant="filled"
                    block
                  >
                    <span>{entry.actionLabel}</span>
                    <ArrowRightOutlined />
                  </Button>
                </Flex>
              </Flex>
            </AnalyticsAction>
          )
        })}
      </Flex>
    </section>
  )
}
